import { sortBy, uniq } from 'lodash'
import type { NamedLocationConsumer, ProviderOption, ProviderRecord } from 'app/types/provider-directory'

/**
 * A provider filter value is `<resourceName>@<locationId>`, the resource name being what a
 * procedure's `providerId` holds (`resources/provider_47`). It repeats at every location of a
 * workspace, each time a different person, so it means nothing without the location it was
 * picked at. `vendorId` is no substitute: some practices' ids drop the `provider_` prefix.
 */
export const PROVIDER_KEY_SEPARATOR = `@`

const QUOTED_KEY = /"([^"]+)"/g

/**
 * One option per provider per location sharing its PMS consumer, named with the location
 * once there is more than one. `query` is either typed text, matched against the name, or
 * `"key" OR "key"`, which is how the picker asks for the labels of a saved selection.
 */
export function providerFilterOptions(
  providers: readonly ProviderRecord[],
  locations: readonly NamedLocationConsumer[],
  query: string = ``,
): ProviderOption[] {
  const entries = providers.flatMap((provider) =>
    locations
      .filter((location) => !!location.kollaConsumerId && location.kollaConsumerId === provider.kollaConsumerId)
      .map((location) => ({
        id: `${provider.resourceName}${PROVIDER_KEY_SEPARATOR}${location.id}`,
        name: provider.displayName || provider.resourceName,
        location: location.name,
      })),
  )
  const manyLocations = uniq(entries.map((entry) => entry.location)).length > 1
  const options = entries.map((entry) => ({
    id: entry.id,
    name: manyLocations ? `${entry.name} (${entry.location})` : entry.name,
  }))

  const keys = Array.from(query.matchAll(QUOTED_KEY), (match) => match[1])
  const text = query.trim().toLowerCase()
  const matching = keys.length
    ? options.filter((option) => keys.includes(option.id))
    : options.filter((option) => option.name.toLowerCase().includes(text))
  return sortBy(matching, (option) => option.name.toLowerCase())
}
